<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="scheduledReportsList"
      :items-per-page="-1"
      :loading="isScheduledReportsListLoading"
      :no-data-text="
        !scheduledReportsList.length ? 'No scheduled reports found' : ''
      "
      :header-props="headerProps"
      :footer-props="footerProps"
      class="elevation-1"
    >
      <!--Headers slot-->
      <template v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }">
        <tr>
          <th
            v-for="(column, i) in columns"
            :key="i"
            class="'text-primary text-subtitle-1 font-weight-bold'"
            @click="() => toggleSort(column)"
          >
            {{ column.title }}
            <template v-if="isSorted(column)">
              <v-icon :icon="getSortIcon(column)" size="sm" />
            </template>
          </th>
        </tr>
      </template>

      <!--Items slot-->
      <template v-slot:item="{ item }">
        <tr>
            <td style="width: 50%">
            <span class="text-primary text-subtitle-1">
              <span class="font-weight-bold">
              {{ item.name }}
              </span>
              — {{ item.baseReport.name }}
            </span>
            </td>
          <td>
            <span class="text-primary text-subtitle-1">{{
              item.schedule.description
            }}</span>
          </td>
          <td>
            <span class="text-primary text-subtitle-1">{{
              mapFrequency(item.schedule.frequency)
            }}</span>
          </td>
          <!-- <td>
            <span class="text-primary text-subtitle-1">{{
              item.schedule.time
            }}</span>
          </td>
          <td>
            <span class="text-primary text-subtitle-1">{{
              mapDayUponFrequency(item.schedule.frequency, item.schedule.day)
            }}</span>
          </td> -->
          <td>
            <ActionButtonsVue
              v-if="!item.isV1"
              :buttonsList="actionButtons"
              :outlined="true"
              :disabled="!userCanUpdate || !userCanCreate || !userCanDelete"
              :item="item"
              class="ml-n2"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <ConfirmDialogDeleteScheduledReports
      v-if="showConfirmDialog"
      :dialog="showConfirmDialog"
      :scheduledReportInfo="ScheduledReportInfo"
      @update:dialog="(value: boolean) => (showConfirmDialog = value)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import { useAbility } from '@/plugins/useAbility.ts';

import { searchByTermFactory } from '@/Core.Patterns/Factory';
import { sortAlphabetically } from '@/Core.Patterns/Strategy';

import ActionButtonsVue from '@/Core.UI.Domain/Components/ActionButtons.vue';
import ConfirmDialogDeleteScheduledReports from '@/Core.Service.Domain/ViewSupport/Reports/Components/ConfirmDialogDeleteScheduledReports.vue';

import { IScheduledReportPayload } from '@/Core.Service.Domain/ViewSupport/Reports/types/scheduledReportPayload.ts';

const store = useStore();
const router = useRouter();

const showConfirmDialog = ref(false);
const ScheduledReportInfo = ref({});

const headerProps = { 'sort-icon': 'mdi-unfold-more-horizontal' };
const footerProps = { 'items-per-page-options': [10, 20, 30, 40, 50] };
// const weekDays = [
//   'Sunday',
//   'Monday',
//   'Tuesday',
//   'Wednesday',
//   'Thursday',
//   'Friday',
//   'Saturday',
// ];

const headers = ref([
  {
    title: 'Name',
    align: 'start' as const,
    value: 'report.name',
    sortable: true,
  },
  {
    title: 'Schedule',
    align: 'start' as const,
    value: 'schedule.description',
    sortable: true,
  },
  {
    title: 'Frequency',
    align: 'start' as const,
    value: 'schedule.frequency',
    sortable: true,
  },
  // {
  //   title: 'Time',
  //   align: 'start' as const,
  //   value: 'schedule.time',
  //   sortable: true,
  // },
  // {
  //   title: 'Day',
  //   align: 'start' as const,
  //   value: 'schedule.day',
  //   sortable: true,
  // },
  {
    title: 'Actions',
    align: 'start' as const,
    value: 'actions',
    sortable: false,
  },
]);

const filteredItems = ref([]);

const searchTerm = computed(
  () => store.getters['ViewSupportStore/ReportsStore/getReportSearchTerm']
);

const ScheduledReportsList = computed(
  () => store.getters['ViewSupportStore/ReportsStore/getScheduledReportsList']
);

const isScheduledReportsListLoading = computed(
  () =>
    store.getters[
      'ViewSupportStore/ReportsStore/getIsScheduledReportsListLoading'
    ]
);

const scheduledReportsList = computed<IScheduledReportPayload[]>(() => {
  return filteredItems.value.length
    ? sortAlphabetically(filteredItems.value)
    : sortAlphabetically(ScheduledReportsList.value, 'name');
});

const ability = useAbility();
const userCanCreate = computed(() => ability.can('create', 'schedules'));
const userCanDelete = computed(() => ability.can('delete', 'schedules'));
const userCanUpdate = computed(() => ability.can('update', 'schedules'));

watch(searchTerm, (newVal) => {
  filteredItems.value = searchByTermFactory(
    newVal,
    ['name'],
    ScheduledReportsList.value
  );
});

function mapFrequency(frequency: number) {
  switch (frequency) {
    case 1:
      return 'Daily';
    case 2:
      return 'Weekly';
    case 3:
      return 'Monthly';
    default:
      return frequency;
  }
}

// function mapDayUponFrequency(frequency: number, day: number) {
//   if (frequency === 1) return ' ';
//   if (frequency === 2) return weekDays[day];
//   return returnDayAsOrdinal(day);
// }

// function returnDayAsOrdinal(day: number) {
//   if (day > 3 && day < 21) return `${day}th`;
//   switch (day % 10) {
//     case 1:
//       return `${day}st`;
//     case 2:
//       return `${day}nd`;
//     case 3:
//       return `${day}rd`;
//     default:
//       return `${day}th`;
//   }
// }

const actionButtons = [
  {
    icon: 'pencil',
    toolTip: 'Edit Scheduled Report',
    action: (item?: unknown) => {
      if (!userCanUpdate.value || !userCanCreate.value || !userCanDelete.value)
        return;
      const scheduledReport = item as IScheduledReportPayload;
      router.push({
        name: 'EditScheduledReport',
        params: { id: scheduledReport.id },
      });
    },
  },
  {
    icon: 'trash-can-outline',
    toolTip: 'Delete Scheduled Report',
    action: (item?: unknown) => {
      if (!userCanUpdate.value || !userCanCreate.value || !userCanDelete.value)
        return;
      ScheduledReportInfo.value = item as IScheduledReportPayload;
      showConfirmDialog.value = true;
    },
  },
];
</script>
